import { campaignVersion, getPuzzle } from '../campaign/data';
import { blankCells } from '../game/engine';
import type { CellState, ProgressSave, SavedAttempt } from '../types';

export const SAVE_KEY = 'mindubi:shibadoku:save:v1';
export const BACKUP_KEY = 'mindubi:shibadoku:save:v1:backup';

export const freshProgress = (): ProgressSave => ({
  schemaVersion: 1,
  campaignVersion,
  revision: 0,
  updatedAt: new Date().toISOString(),
  completedLevelIds: [],
  attemptsByLevelId: {},
  bestTimesMs: {},
  selectedCharacterId: null,
  mapScrollY: 0,
  tutorialCompleted: false,
  settings: { soundEffects: true, reducedMotion: 'system', regionPatterns: true },
});

export const newAttempt = (levelId: number): SavedAttempt => {
  const puzzle = getPuzzle(levelId);
  return {
    puzzleId: puzzle.id,
    puzzleHash: puzzle.contentHash,
    cells: blankCells(puzzle.size, puzzle.givenShibas),
    undo: [], elapsedMs: 0, status: 'not-started',
  };
};

const isCellState = (value: unknown): value is CellState => value === 'empty' || value === 'excluded' || value === 'shiba';

export function validateProgress(value: unknown): ProgressSave | null {
  if (!value || typeof value !== 'object') return null;
  const save = value as ProgressSave;
  if (save.schemaVersion !== 1 || save.campaignVersion !== campaignVersion || !Array.isArray(save.completedLevelIds)) return null;
  const completed = [...new Set(save.completedLevelIds.filter((id) => Number.isInteger(id) && id >= 1 && id <= 135))].sort((a,b) => a-b);
  const prefix: number[] = [];
  for (let id = 1; id <= 135 && completed.includes(id); id += 1) prefix.push(id);
  const attempts: Record<string, SavedAttempt> = {};
  if (save.attemptsByLevelId && typeof save.attemptsByLevelId === 'object') {
    Object.entries(save.attemptsByLevelId).forEach(([key, attempt]) => {
      const levelId = Number(key); const puzzle = getPuzzle(levelId);
      if (!puzzle || !attempt || attempt.puzzleId !== puzzle.id || attempt.puzzleHash !== puzzle.contentHash) return;
      if (!Array.isArray(attempt.cells) || attempt.cells.length !== puzzle.size || !attempt.cells.every((row) => Array.isArray(row) && row.length === puzzle.size && row.every(isCellState))) return;
      // Normalize older saves too: legacy assistance moves remain undoable.
      attempts[key] = {
        puzzleId: attempt.puzzleId, puzzleHash: attempt.puzzleHash, cells: attempt.cells,
        status: attempt.status,
        undo: Array.isArray(attempt.undo) ? attempt.undo.slice(-200).map((move) => ({ source: 'player', changes: move.changes })) : [],
        elapsedMs: Math.max(0, Number(attempt.elapsedMs) || 0),
      };
    });
  }
  return {
    ...freshProgress(), ...save,
    completedLevelIds: prefix,
    attemptsByLevelId: attempts,
    revision: Math.max(0, Number(save.revision) || 0),
    mapScrollY: Math.max(0, Math.min(14400, Number(save.mapScrollY) || 0)),
    settings: {
      soundEffects: save.settings?.soundEffects !== false,
      reducedMotion: ['system','on','off'].includes(save.settings?.reducedMotion) ? save.settings.reducedMotion : 'system',
      regionPatterns: save.settings?.regionPatterns !== false,
    },
  };
}

export function loadProgress(): { progress: ProgressSave; recovered: boolean; error: string | null } {
  try {
    const primary = localStorage.getItem(SAVE_KEY);
    if (primary) {
      const valid = validateProgress(JSON.parse(primary));
      if (valid) return { progress: valid, recovered: false, error: null };
    }
    const backup = localStorage.getItem(BACKUP_KEY);
    if (backup) {
      const valid = validateProgress(JSON.parse(backup));
      if (valid) return { progress: valid, recovered: true, error: 'A sérült fő mentés helyett a biztonsági mentést töltöttük be.' };
    }
  } catch { return { progress: freshProgress(), recovered: false, error: 'A mentés nem volt olvasható. Új helyi játék indult.' }; }
  return { progress: freshProgress(), recovered: false, error: null };
}

export function persistProgress(progress: ProgressSave): { progress: ProgressSave; error: string | null } {
  const next = { ...progress, revision: progress.revision + 1, updatedAt: new Date().toISOString() };
  try {
    const previous = localStorage.getItem(SAVE_KEY);
    if (previous && validateProgress(JSON.parse(previous))) localStorage.setItem(BACKUP_KEY, previous);
    localStorage.setItem(SAVE_KEY, JSON.stringify(next));
    return { progress: next, error: null };
  } catch {
    return { progress: next, error: 'A böngésző most nem tudja elmenteni a haladásodat. Az oldal bezárásakor az új eredmények elveszhetnek.' };
  }
}

export function clearProgress() {
  localStorage.removeItem(SAVE_KEY); localStorage.removeItem(BACKUP_KEY);
}
